import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronRight, MapPin } from 'lucide-react';

/**
 * BoardContractsRecap — 22/07/2026 (retour user). Bloc « contrats honorés
 * vs laissés filer » par tranche de valeur, affiché au Conseil annuel à
 * côté du recap AO. Données : annualReport.contractDeals
 * (cf. contractAcceptanceStats.js).
 *
 * 25/07/2026 (retour user) — drill-down : chaque tranche est dépliable
 * (détail contrat par contrat avec badge Hors zone + catégorie) et un
 * second tableau agrège par catégorie (Frigo/BTP/Export…).
 */

const rateClass = (rate) => rate === null ? 'text-slate-600'
  : rate >= 75 ? 'text-emerald-300'
  : rate >= 50 ? 'text-amber-300'
  : 'text-danger';

function BucketDetail({ entries }) {
  if (!entries || entries.length === 0) {
    return <p className="text-[10px] text-slate-600 italic py-1">Aucun détail disponible pour cette tranche.</p>;
  }
  return (
    <div className="max-h-44 overflow-y-auto space-y-0.5 pr-1">
      {entries.map((e, i) => (
        <div
          key={`${e.name}-${e.spawnDay}-${i}`}
          className="flex items-center gap-2 text-[10px] py-0.5 border-b border-ink-700/40 last:border-0"
          data-testid="contracts-detail-row"
        >
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.taken ? 'bg-emerald-400' : 'bg-slate-600'}`} />
          <span className="text-slate-300 truncate flex-1">{e.name}</span>
          <span className="px-1.5 py-px rounded bg-ink-600/60 text-slate-400 shrink-0">{e.category}</span>
          {e.horsZone && (
            <span className="px-1.5 py-px rounded bg-amber-500/15 text-amber-300 font-bold shrink-0 flex items-center gap-0.5" data-testid="badge-hors-zone">
              <MapPin className="w-2.5 h-2.5" /> Hors zone
            </span>
          )}
          <span className="font-mono text-slate-400 shrink-0">{(e.value || 0).toLocaleString('fr-FR')} €</span>
          <span className={`font-bold shrink-0 ${e.taken ? 'text-emerald-300' : 'text-slate-500'}`}>
            {e.taken ? 'Honoré' : 'Filé'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function BoardContractsRecap({ stats }) {
  const [openBucket, setOpenBucket] = useState(null);
  if (!stats || (stats.totalTaken + stats.totalMissed) === 0) return null;
  const rate = stats.acceptanceRate;

  const advice = rate === null ? ''
    : rate >= 75 ? 'Très bon taux de captation — la capacité suit la demande.'
    : rate >= 50 ? `Vous laissez filer ${100 - rate} % des contrats décidés — manque de chauffeurs/camions, ou offres non pertinentes ? À creuser tranche par tranche ci-dessous.`
    : 'Majorité des contrats perdus faute d\'acceptation. Le conseil demande un plan capacité (recrutements, flotte) ou un recentrage sur les tranches rentables.';

  return (
    <div className="bg-ink-700/30 rounded-xl p-4 border border-white/5 space-y-3" data-testid="board-contracts-recap">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-sky-300" />
          <p className="text-xs uppercase tracking-widest text-slate-300 font-mono font-bold">
            Contrats honorés vs laissés filer
          </p>
        </div>
        <p className={`text-sm font-black font-mono ${rateClass(rate)}`} data-testid="contracts-acceptance-rate">
          {rate === null ? '—' : `${rate} %`}
        </p>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-slate-500 uppercase">
            <th className="text-left py-1">Tranche (valeur contrat)</th>
            <th className="text-right py-1">Honorés</th>
            <th className="text-right py-1">Laissés filer</th>
            <th className="text-right py-1">Taux</th>
          </tr>
        </thead>
        <tbody>
          {stats.rows.map(r => (
            <React.Fragment key={r.id}>
              <tr
                className="border-t border-ink-600/60 cursor-pointer hover:bg-ink-600/20"
                data-testid={`contracts-bucket-${r.id}`}
                onClick={() => setOpenBucket(openBucket === r.id ? null : r.id)}
              >
                <td className="py-1.5 text-slate-300 flex items-center gap-1">
                  {openBucket === r.id
                    ? <ChevronDown className="w-3 h-3 text-slate-500" />
                    : <ChevronRight className="w-3 h-3 text-slate-500" />}
                  {r.label}
                </td>
                <td className="py-1.5 text-right font-mono text-emerald-300">{r.taken}</td>
                <td className="py-1.5 text-right font-mono text-slate-400">{r.missed}</td>
                <td className={`py-1.5 text-right font-mono font-bold ${rateClass(r.rate)}`}>
                  {r.rate === null ? '—' : `${r.rate} %`}
                </td>
              </tr>
              {openBucket === r.id && (
                <tr>
                  <td colSpan={4} className="bg-ink-800/40 rounded px-2 py-1.5" data-testid={`contracts-bucket-detail-${r.id}`}>
                    <BucketDetail entries={r.entries} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {stats.byCategory && stats.byCategory.length > 0 && (
        <div data-testid="contracts-by-category">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Par catégorie</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            {stats.byCategory.map(c => (
              <div key={c.category} className="flex items-center justify-between text-[10px] border-b border-ink-700/40 py-0.5">
                <span className="text-slate-300 truncate">{c.category}</span>
                <span className="font-mono shrink-0 ml-2">
                  <span className="text-emerald-300">{c.taken}</span>
                  <span className="text-slate-600"> / </span>
                  <span className="text-slate-400">{c.missed}</span>
                  <span className={`ml-1.5 font-bold ${rateClass(c.rate)}`}>{c.rate === null ? '—' : `${c.rate} %`}</span>
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-600 mt-0.5">Honorés / laissés filer par spécialité — cliquez une tranche ci-dessus pour le détail contrat par contrat (badge Hors zone inclus).</p>
        </div>
      )}

      <p className="text-[11px] text-slate-400 italic">{advice}</p>
      {stats.partial && (
        <p className="text-[10px] text-slate-600">
          Historique partiel — la mémoire commerciale couvre les ~2 000 dernières annonces.
        </p>
      )}
    </div>
  );
}
